import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import usePageMetadata from '../utils/usePageMetadata';
// InsightsFeed removed - replaced with alerting system
import './Growth.css';

const Growth = () => {
  usePageMetadata({
    title: 'Growth & Alerts | The Ad Shop',
    description: 'Monitor your site performance with automated alerts and growth insights.'
  });

  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine active tab from route - safe initialization
  const [activeTab, setActiveTab] = useState(() => {
    try {
      if (typeof window !== 'undefined' && window.location) {
        const pathname = window.location.pathname;
        if (pathname === '/growth/alerts') return 'alerts';
        if (pathname === '/growth') return 'alerts';
      }
    } catch (error) {
      console.error('Error initializing activeTab:', error);
    }
    return 'alerts';
  });
  
  // Update tab when route changes
  useEffect(() => {
    try {
      if (location && location.pathname) {
        if (location.pathname === '/growth/alerts') {
          setActiveTab('alerts');
        } else if (location.pathname === '/growth') {
          setActiveTab('alerts');
        }
      }
    } catch (error) {
      console.error('Error updating activeTab:', error);
    }
  }, [location]);
  
  // Handle tab change - navigate to correct route
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'alerts') {
      navigate('/growth/alerts');
    } else {
      navigate('/growth/alerts');
    }
  };
  
  // Sites state (for alerts)
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);

  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertsError, setAlertsError] = useState('');
  const [success, setSuccess] = useState('');

  const isPaidPlan = user?.plan && user.plan !== 'free';

  const [alertConfig, setAlertConfig] = useState({
    rpmDropThreshold: 10,
    trafficDropThreshold: 10,
    frequency: 'weekly',
    enabled: false
  });

  // Fetch sites
  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get('/api/sites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sitesData = response.data.sites;
      setSites(sitesData);
      if (sitesData.length > 0) {
        setSelectedSite(sitesData[0]);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      // Error handled silently
    }
  }, [token]);

  // fetchInsights removed - insights no longer exist

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    if (!isPaidPlan) {
      setAlertsLoading(false);
      return;
    }

    setAlertsLoading(true);
    setAlertsError('');
    try {
      const response = await axios.get('/api/alerts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.alerts) {
        setAlerts(response.data.alerts);
        if (response.data.alerts.length > 0) {
          setAlertConfig(response.data.alerts[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      if (error.response?.status !== 404) {
        setAlertsError('Failed to load alerts configuration');
      }
    } finally {
      setAlertsLoading(false);
    }
  }, [token, isPaidPlan]);

  useEffect(() => {
    if (token) {
      fetchSites();
    }
  }, [token, fetchSites]);

  // Insights removed - no longer needed

  useEffect(() => {
    if (token && activeTab === 'alerts') {
      fetchAlerts();
    }
  }, [token, activeTab, fetchAlerts]);

  const handleSave = async () => {
    if (!isPaidPlan) return;

    setSaving(true);
    setAlertsError('');
    setSuccess('');
    try {
      await axios.post('/api/alerts', alertConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Alerts configuration saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving alerts:', error);
      setAlertsError('Failed to save alerts configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (alertId) => {
    if (!isPaidPlan) return;

    setSaving(true);
    setAlertsError('');
    try {
      await axios.delete(`/api/alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchAlerts();
      setSuccess('Alert deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting alert:', error);
      setAlertsError('Failed to delete alert');
    } finally {
      setSaving(false);
    }
  };

  // handleDismissInsight removed - insights no longer exist


  // Safety check - don't render until location is available
  if (!location || !location.pathname) {
    return (
      <div className="growth-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return <div className="growth-page">Please log in to view insights and alerts.</div>;
  }

  return (
    <div className="growth-page">
      <div className="growth-container">
        <div className="growth-header">
          <h1>Insights and Alerts</h1>
          <p className="growth-subtitle">
            Insights and alerts to help you grow your site
          </p>
        </div>

        {/* Site Selector - Above tabs */}
        {sites.length > 0 && (
          <div className="site-selector">
            <label htmlFor="site-select">Site:</label>
            <select
              id="site-select"
              value={selectedSite?.siteId || ''}
              onChange={(e) => {
                const site = sites.find(s => s.siteId === e.target.value);
                setSelectedSite(site);
              }}
            >
              {sites.map(site => (
                <option key={site.siteId} value={site.siteId}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tab Navigation - Only Alerts now */}
        <div className="growth-tabs">
          <button
            className={`growth-tab ${activeTab === 'alerts' ? 'active' : ''}`}
            onClick={() => handleTabChange('alerts')}
          >
            <span className="tab-icon">🔔</span>
            Alerts
            {!isPaidPlan && <span className="tab-badge">Pro</span>}
          </button>
        </div>

        {/* Alerts Tab Content */}
        {activeTab === 'alerts' && (
          <div className="growth-tab-content">
            {!isPaidPlan ? (
              <div className="upgrade-prompt">
                <div className="upgrade-icon">🔔</div>
                <h2>Upgrade to Enable Email Alerts</h2>
                <p>
                  Email alerts are available for paid plans. Set up automated notifications 
                  when your RPM drops, traffic changes, or other important metrics shift.
                </p>
                <div className="upgrade-features">
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Configure custom alert thresholds</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Daily or weekly email frequency</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Monitor RPM and traffic changes</span>
                  </div>
                </div>
                <Link to="/billing" className="upgrade-button">
                  Upgrade Plan
                </Link>
              </div>
            ) : (
              <>
                {alertsError && (
                  <div className="alert-message error">
                    {alertsError}
                  </div>
                )}

                {success && (
                  <div className="alert-message success">
                    {success}
                  </div>
                )}

                {alertsLoading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading alerts configuration...</p>
                  </div>
                ) : (
                  <div className="alerts-config">
                    <div className="config-card">
                      <div className="config-header">
                        <h2>Alert Configuration</h2>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={alertConfig.enabled}
                            onChange={(e) => setAlertConfig({ ...alertConfig, enabled: e.target.checked })}
                          />
                          <span className="toggle-slider"></span>
                          <span className="toggle-label">{alertConfig.enabled ? 'Enabled' : 'Disabled'}</span>
                        </label>
                      </div>

                      <div className={`config-content ${!alertConfig.enabled ? 'disabled' : ''}`}>
                        <div className="config-section">
                          <label className="config-label">
                            RPM Drop Threshold (%)
                            <span className="config-help">Alert when RPM drops by this percentage</span>
                          </label>
                          <div className="threshold-input">
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={alertConfig.rpmDropThreshold}
                              onChange={(e) => setAlertConfig({ 
                                ...alertConfig, 
                                rpmDropThreshold: parseInt(e.target.value) || 0 
                              })}
                              disabled={!alertConfig.enabled}
                            />
                            <span className="input-suffix">%</span>
                          </div>
                        </div>

                        <div className="config-section">
                          <label className="config-label">
                            Traffic Drop Threshold (%)
                            <span className="config-help">Alert when traffic drops by this percentage</span>
                          </label>
                          <div className="threshold-input">
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={alertConfig.trafficDropThreshold}
                              onChange={(e) => setAlertConfig({ 
                                ...alertConfig, 
                                trafficDropThreshold: parseInt(e.target.value) || 0 
                              })}
                              disabled={!alertConfig.enabled}
                            />
                            <span className="input-suffix">%</span>
                          </div>
                        </div>

                        <div className="config-section">
                          <label className="config-label">
                            Email Frequency
                            <span className="config-help">How often to check and send alerts</span>
                          </label>
                          <div className="frequency-options">
                            <label className="radio-option">
                              <input
                                type="radio"
                                name="frequency"
                                value="daily"
                                checked={alertConfig.frequency === 'daily'}
                                onChange={(e) => setAlertConfig({ ...alertConfig, frequency: e.target.value })}
                                disabled={!alertConfig.enabled}
                              />
                              <span>Daily</span>
                            </label>
                            <label className="radio-option">
                              <input
                                type="radio"
                                name="frequency"
                                value="weekly"
                                checked={alertConfig.frequency === 'weekly'}
                                onChange={(e) => setAlertConfig({ ...alertConfig, frequency: e.target.value })}
                                disabled={!alertConfig.enabled}
                              />
                              <span>Weekly</span>
                            </label>
                          </div>
                        </div>

                        <button 
                          className="save-button"
                          onClick={handleSave}
                          disabled={saving || !alertConfig.enabled}
                        >
                          {saving ? 'Saving...' : 'Save Configuration'}
                        </button>
                        
                        {!alertConfig.enabled && (
                          <p className="config-disabled-note">
                            Enable alerts above to configure and save your alert settings.
                          </p>
                        )}
                      </div>
                    </div>

                    {alerts.length > 0 && (
                      <div className="alerts-list">
                        <h2>Active Alerts</h2>
                        {alerts.map((alert) => (
                          <div key={alert._id || alert.id} className="alert-item">
                            <div className="alert-info">
                              <h3>{alert.name || 'Alert'}</h3>
                              <p>Frequency: {alert.frequency}</p>
                              <p>Status: {alert.enabled ? 'Active' : 'Inactive'}</p>
                            </div>
                            <button 
                              className="delete-button"
                              onClick={() => handleDelete(alert._id || alert.id)}
                              disabled={saving}
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Growth;

